import { Trans, useLingui } from '@lingui/react/macro';
import { ImageOff, Loader } from 'lucide-react';
import { ChampionshipMediaTab } from 'modules/championship/domain/enums/championship-media-tab';
import { useChampionshipMediaJobs } from 'modules/championship/ui/hooks/use-championship-media-jobs';
import { PublishPostModal } from 'modules/integrations/ui/components';
import { MediaGenerationJob } from 'modules/manager/domain/graphql/media-generation-jobs';
import {
  MEDIA_GENERATION_JOB_STATUS_LABELS,
  canPublishMediaGenerationJob,
  isActiveMediaGenerationJob,
  mediaGenerationJobHideAction,
} from 'modules/manager/domain/media-generation-job';
import { mediaKindLabel } from 'modules/manager/domain/media-kind';
import {
  useDeleteMediaGenerationJobMutation,
  useDiscardMediaGenerationJobMutation,
} from 'modules/manager/store/media-generation-jobs-api';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deriveAsyncState } from 'shared/utils/async-state';
import { UDEntityList } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

interface ChampionshipMediaResultsProps {
  championshipId: number;
  /** Организация соревнования (`tournamentId` бэкенда): по ней фильтруется очередь и выбирается канал. */
  organizerId: number;
}

/**
 * Подвкладка «Картинки» вкладки «Медиа» турнира: результаты генерации этого соревнования
 * с публикацией в Telegram. Публикует админ организации — «Очередь медиа» только для
 * супер-админа (`adminOnly`), и кнопки «Опубликовать» там нет (IMGM-1488).
 */
const ChampionshipMediaResults = ({ championshipId, organizerId }: ChampionshipMediaResultsProps) => {
  const { t } = useLingui();
  const { data, currentData, isFetching, isError } = useChampionshipMediaJobs(championshipId, organizerId);
  const [previewJobId, setPreviewJobId] = useState<number | null>(null);
  const [jobToPublish, setJobToPublish] = useState<MediaGenerationJob | null>(null);
  const [jobToHide, setJobToHide] = useState<MediaGenerationJob | null>(null);
  const [discardJob, { isLoading: isDiscarding }] = useDiscardMediaGenerationJobMutation();
  const [deleteJob, { isLoading: isDeleting }] = useDeleteMediaGenerationJobMutation();

  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const items = useMemo(() => data?.items ?? [], [data]);
  const previewJob = items.find((job) => job.id === previewJobId) ?? null;

  // Ищем по тому, что видно в строке: тип медиа и объект (матч, тур, группа).
  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((job) => `${t(mediaKindLabel(job.kind))} ${job.subjectTitle}`.toLowerCase().includes(query));
  }, [items, search, t]);

  // Считаем по отфильтрованному списку, иначе ветка «не найдено» не покажется никогда.
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: filteredItems.length,
        hasCurrentData: currentData !== undefined,
        isFetching,
        isError,
      }),
    [filteredItems.length, currentData, isFetching, isError],
  );

  /**
   * «Скрыть» для админа — одно действие, а на бэкенде два: черновик отклоняется (след остаётся в
   * очереди супер-админа), ошибка удаляется. Список после мутации перечитывается по тегу, а
   * скрытое в него не попадает — фильтр по статусам в `useChampionshipMediaJobs`.
   */
  const handleHide = async () => {
    if (!jobToHide) return;
    const action = mediaGenerationJobHideAction(jobToHide);
    try {
      if (action === 'discard') await discardJob(jobToHide.id).unwrap();
      if (action === 'delete') await deleteJob(jobToHide.id).unwrap();
      if (previewJobId === jobToHide.id) setPreviewJobId(null);
      setJobToHide(null);
    } catch (error) {
      console.error('Failed to hide media job:', error);
      toast.error(t`Не удалось скрыть картинку. Попробуйте ещё раз.`);
    }
  };

  const goToTemplates = useCallback(
    () => navigate(Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, ChampionshipMediaTab.TEMPLATES)),
    [navigate, championshipId],
  );

  const renderThumb = (job: MediaGenerationJob) => {
    if (job.image?.externalUrl) {
      return job.image.externalUrl;
    }
    return (
      <div className="bg-surfaceSunken text-inkMuted flex h-full w-full items-center justify-center">
        {isActiveMediaGenerationJob(job) ? <Loader size={20} aria-hidden /> : <ImageOff size={20} aria-hidden />}
      </div>
    );
  };

  return (
    // Эталон — «Список внутри карточки» (`.claude/rules/design-system.md` §2): `UDEntityList` сам рисует
    // скелетон, `UDEmptyState` по центру для «пусто» / «не найдено» и ошибку. Цепочка flex от страницы
    // не рвётся, иначе пустое состояние прилипнет к тулбару.
    <section className="flex min-h-0 flex-1 flex-col" data-testid="championship-media-results">
      <UDEntityList<MediaGenerationJob>
        items={filteredItems}
        getItemKey={(job) => job.id}
        state={state}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по типу или матчу`}
        emptyText={t`Картинок пока нет`}
        emptySubtitle={t`Сгенерируйте их на вкладке «Шаблоны» — готовые появятся здесь, отсюда их можно опубликовать в Telegram.`}
        emptyAction={
          <UDButton variant="primary" type="button" onClick={goToTemplates} data-testid="media-results-go-templates">
            <Trans>Перейти к шаблонам</Trans>
          </UDButton>
        }
        notFoundText={t`Картинки не найдены`}
        errorText={t`Не удалось загрузить картинки`}
        renderItem={(job) => (
          <UDEntityRow
            data-testid={`media-result-row-${job.id}`}
            avatar={renderThumb(job)}
            title={t(mediaKindLabel(job.kind))}
            subtitle={job.error || job.subjectTitle || job.text || undefined}
            onClick={() => setPreviewJobId(job.id)}
            actions={
              <>
                <UDText type="footnote" className="text-inkMuted">
                  {t(MEDIA_GENERATION_JOB_STATUS_LABELS[job.status])}
                </UDText>
                {canPublishMediaGenerationJob(job) ? (
                  <UDButton
                    variant="primary"
                    type="button"
                    onClick={() => setJobToPublish(job)}
                    data-testid={`publish-result-${job.id}`}
                  >
                    <Trans>Опубликовать</Trans>
                  </UDButton>
                ) : null}
                {mediaGenerationJobHideAction(job) ? (
                  <UDButton variant="secondary" type="button" onClick={() => setJobToHide(job)} data-testid={`hide-result-${job.id}`}>
                    <Trans>Скрыть</Trans>
                  </UDButton>
                ) : null}
              </>
            }
          />
        )}
      />

      {previewJob ? (
        <Modal show onClose={() => setPreviewJobId(null)} size="lg">
          <Modal.Header title={t(mediaKindLabel(previewJob.kind))} />
          <Modal.Body>
            {previewJob.image?.externalUrl ? (
              <img
                src={previewJob.image.externalUrl}
                alt=""
                className="mx-auto max-h-[70vh] w-auto max-w-full"
                data-testid="media-result-preview-image"
              />
            ) : (
              <UDText type="subhead" className="text-inkSecondary py-10 text-center">
                {isActiveMediaGenerationJob(previewJob) ? <Trans>Картинка ещё генерируется</Trans> : <Trans>Нет картинки</Trans>}
              </UDText>
            )}
          </Modal.Body>
          <Modal.Footer>
            <UDButton variant="secondary" type="button" onClick={() => setPreviewJobId(null)}>
              <Trans>Закрыть</Trans>
            </UDButton>
            {mediaGenerationJobHideAction(previewJob) ? (
              <UDButton
                variant="secondary"
                type="button"
                onClick={() => setJobToHide(previewJob)}
                data-testid="media-result-preview-hide"
              >
                <Trans>Скрыть</Trans>
              </UDButton>
            ) : null}
            {canPublishMediaGenerationJob(previewJob) ? (
              <UDButton
                variant="primary"
                type="button"
                onClick={() => {
                  setPreviewJobId(null);
                  setJobToPublish(previewJob);
                }}
                data-testid="media-result-preview-publish"
              >
                <Trans>Опубликовать</Trans>
              </UDButton>
            ) : null}
          </Modal.Footer>
        </Modal>
      ) : null}

      {jobToPublish?.image ? (
        <PublishPostModal
          organizerId={organizerId}
          imageUrl={jobToPublish.image.externalUrl}
          defaultText={jobToPublish.text}
          onClose={() => setJobToPublish(null)}
        />
      ) : null}

      <Modal.Confirm
        show={jobToHide !== null}
        title={t`Скрыть картинку?`}
        description={t`Она пропадёт из списка, вернуть её будет нельзя. Нужна снова — сгенерируйте заново.`}
        confirmText={t`Скрыть`}
        isLoading={isDiscarding || isDeleting}
        onConfirm={() => void handleHide()}
        onCancel={() => setJobToHide(null)}
      />
    </section>
  );
};

export default ChampionshipMediaResults;
